<template>
  <div class="booking-fee-options">
    <v-row>
      <v-col class="py-0">
        <div>
          <span v-if="showLabel" class="subheading ml-3">Avgifter</span>
          <div class="d-flex">
            <v-switch
              v-if="showPositionReservationFeeSwitch"
              v-model="positionReservationSelected"
              :label="positionReservationLabel"
              hide-details
              class="mt-0 mr-6 booking-fee-switch flex-grow-0 flex-shrink-1"
              color="secondary"
              style="width: auto;"
              @change="selectedPositionReservation"
            ></v-switch>

            <v-switch
              v-if="showBookingFeeSwitch"
              v-model="bookingFeeSelected"
              :label="bookingFeeLabel"
              hide-details
              class="mt-0 mr-6 booking-fee-switch flex-grow-0 flex-shrink-1"
              color="secondary"
              style="width: auto;"
              @change="selectedBookingFee"
            ></v-switch>
            <v-switch
              v-if="showElectricityFeeSwitch"
              v-model="electricityFeeSelected"
              label="Lägg till elektricitet"
              hide-details
              class="mt-0 booking-fee-switch flex-grow-0 flex-shrink-1"
              color="secondary"
              style="width: auto;"
            ></v-switch>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
const BOOKING_FEE = 'booking_fee'
const POSITION_RESERVATION_FEE = 'position_reservation_fee'
const ELECTRICITY_FEE = 'electricity_fee'

export default {
  name: 'BookingFeeOptions',
  props: {
    value: Array,
    booking: Object,
    bookingGroup: Object,
    availableFeeOptions: {
      type: Array,
      default: null
    },
    isPartOfBookingGroup: {
      type: Boolean,
      default: false
    },
    isRegularBooking: {
      type: Boolean,
      default: false
    },
    isSeasonBooking: {
      type: Boolean,
      default: false
    },
    hideLabel: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    bookingFeeIsManualSelection: false,
    positionReservationIsManualSelection: false
  }),
  computed: {
    feeOptions: function () {
      if (this.availableFeeOptions) {
        return this.filteredFeeOptions(this.availableFeeOptions)
      }

      if (!this.bookingGroup) {
        return this.filteredFeeOptions(this.booking?.feeOptions ?? this.booking?.fee_options ?? this.booking?.category?.fee_options ?? [])
      }

      const feeOptionsByKey = new Map()
      this.bookingGroup.bookings.forEach(booking => {
        const bookingFeeOptions = booking.feeOptions ?? booking.fee_options ?? booking.category?.fee_options ?? []
        bookingFeeOptions.forEach(feeOption => {
          if (!feeOptionsByKey.has(feeOption.key)) {
            feeOptionsByKey.set(feeOption.key, feeOption)
          }
        })
      })

      return Array.from(feeOptionsByKey.values())
        .filter(feeOption => feeOption.key === BOOKING_FEE)
    },
    feeOptionKeys: function () {
      return this.feeOptions.map(feeOption => feeOption.key)
    },
    bookingFeePrice: function () {
      return this.feeOptions.find(feeOption => feeOption.key === BOOKING_FEE)?.price ?? 0
    },
    positionReservationFeePrice: function () {
      return this.feeOptions.find(feeOption => feeOption.key === POSITION_RESERVATION_FEE)?.price ?? 0
    },
    isExistingBooking: function () {
      return !!this.booking?.id || !!this.bookingGroup?.id
    },
    showBookingFeeSwitch: function () {
      return this.feeOptionKeys.includes(BOOKING_FEE) &&
        (!!this.bookingGroup || this.isRegularBooking)
    },
    showPositionReservationFeeSwitch: function () {
      return !!this.booking && !this.isSeasonBooking
    },
    showElectricityFeeSwitch: function () {
      return this.feeOptionKeys.includes(ELECTRICITY_FEE) &&
        !this.isExistingRegularBooking &&
        (!!this.bookingGroup || this.isRegularBooking)
    },
    isExistingRegularBooking: function () {
      return !!this.booking?.id && !this.bookingGroup
    },
    hasVisibleFeeOption: function () {
      return this.showPositionReservationFeeSwitch ||
        this.showBookingFeeSwitch ||
        this.showElectricityFeeSwitch
    },
    showLabel: function () {
      return !this.hideLabel && this.hasVisibleFeeOption
    },
    positionReservationLabel: function () {
      return this.showPositionReservationFeeSwitch
        ? `Platsreservationsavgift (${this.positionReservationFeePrice / 100}kr)`
        : 'Platsreservation'
    },
    bookingFeeLabel: function () {
      return `Bokningsavgift (${this.bookingFeePrice / 100}kr)`
    },
    appliedFees: {
      get: function () {
        return this.value ?? []
      },
      set: function (val) {
        this.$emit('input', val)
      }
    },
    bookingFeeSelected: {
      get: function () {
        return this.appliedFees.includes(BOOKING_FEE)
      },
      set: function (val) {
        this.setAppliedFeeSelection(BOOKING_FEE, val)
      }
    },
    positionReservationSelected: {
      get: function () {
        return this.appliedFees.includes(POSITION_RESERVATION_FEE)
      },
      set: function (val) {
        this.setAppliedFeeSelection(POSITION_RESERVATION_FEE, val)
      }
    },
    electricityFeeSelected: {
      get: function () {
        return this.appliedFees.includes(ELECTRICITY_FEE)
      },
      set: function (val) {
        this.setAppliedFeeSelection(ELECTRICITY_FEE, val)
      }
    }
  },
  methods: {
    filteredFeeOptions: function (feeOptions) {
      return this.isPartOfBookingGroup
        ? feeOptions.filter(feeOption => feeOption.key !== BOOKING_FEE)
        : feeOptions
    },
    appliedFeesWithSelection: function (appliedFees, fee, isSelected) {
      // Build from the supplied array so multiple fee changes can be combined before emitting.
      if (isSelected) {
        return appliedFees.includes(fee)
          ? appliedFees
          : [...appliedFees, fee]
      }

      return appliedFees.includes(fee)
        ? appliedFees.filter(appliedFee => appliedFee !== fee)
        : appliedFees
    },
    setAppliedFeeSelection: function (fee, isSelected) {
      const updatedAppliedFees = this.appliedFeesWithSelection(this.appliedFees, fee, isSelected)
      // Avoid redundant input events and the resulting charge refresh.
      if (updatedAppliedFees !== this.appliedFees) {
        this.appliedFees = updatedAppliedFees
      }
    },
    selectedBookingFee: function () {
      this.bookingFeeIsManualSelection = true
    },
    selectedPositionReservation: function () {
      this.positionReservationIsManualSelection = true
    },
    resetManualSelections: function () {
      this.bookingFeeIsManualSelection = false
      this.positionReservationIsManualSelection = false
    },
    autoToggleFees: function () {
      // Both computed setters read the same value prop until Vue updates the parent.
      // Apply both decisions locally and emit once to prevent the second fee from overwriting the first.
      const bookingFeeSelection = this.autoBookingFeeSelection()
      const positionReservationSelection = this.autoPositionReservationSelection()
      const updatedAppliedFees = [
        [BOOKING_FEE, bookingFeeSelection],
        [POSITION_RESERVATION_FEE, positionReservationSelection]
      ].reduce((appliedFees, [fee, isSelected]) => {
        return isSelected === null
          ? appliedFees
          : this.appliedFeesWithSelection(appliedFees, fee, isSelected)
      }, this.appliedFees)

      if (updatedAppliedFees !== this.appliedFees) {
        this.appliedFees = updatedAppliedFees
      }
    },
    autoBookingFeeSelection: function () {
      // null means that automatic toggling must leave the current selection unchanged.
      if (!this.showBookingFeeSwitch || this.bookingFeeIsManualSelection || this.isExistingBooking || this.isSeasonBooking) {
        return null
      }

      const checkIn = this.bookingGroup?.checkIn ?? this.booking?.checkIn
      return checkIn ? !checkIn.isToday() : null
    },
    autoToggleBookingFee: function () {
      const isSelected = this.autoBookingFeeSelection()
      if (isSelected !== null) {
        this.bookingFeeSelected = isSelected
      }
    },
    autoPositionReservationSelection: function () {
      // null means that automatic toggling must leave the current selection unchanged.
      if (!this.positionReservationIsManualSelection && !this.isExistingBooking && !!this.booking?.position && !this.isSeasonBooking) {
        return !this.booking.checkIn.isToday() && this.booking.position.id !== 0
      }

      return null
    },
    autoTogglePositionReservation: function () {
      const isSelected = this.autoPositionReservationSelection()
      if (isSelected !== null) {
        this.positionReservationSelected = isSelected
      }
    },
    clearSeasonFees: function () {
      // Clear all related fees in one input event for the same reason as autoToggleFees.
      const feesToClear = [BOOKING_FEE, POSITION_RESERVATION_FEE, ELECTRICITY_FEE]
      const updatedAppliedFees = this.appliedFees.filter(fee => !feesToClear.includes(fee))
      if (updatedAppliedFees.length !== this.appliedFees.length) {
        this.appliedFees = updatedAppliedFees
      }
    }
  },
  watch: {
    'booking.checkIn': function () {
      this.autoToggleFees()
    },
    'bookingGroup.checkIn': function () {
      this.autoToggleBookingFee()
    },
    'booking.position': function () {
      this.autoTogglePositionReservation()
    },
    booking: function () {
      this.resetManualSelections()
    },
    bookingGroup: function () {
      this.resetManualSelections()
    },
    isSeasonBooking: function () {
      if (this.isSeasonBooking) {
        this.clearSeasonFees()
      }
    }
  },
  mounted () {
    this.autoToggleFees()
  }
}
</script>
